import { useState } from "react";
import { Container, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { Policy, PolicyVehicle } from "@shared/schema";
import { AssetSectionHeader } from "./AssetSectionHeader";
import { AssetStatusBadge, statusForAsset } from "./AssetStatusBadge";
import { isNonOwnedAttachedTrailer, vehicleValue, type VehicleEntry } from "./asset-helpers";
import { VehicleDialog } from "./VehicleDialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useUpdatePolicyTrailers } from "@/lib/api";

interface Props {
  trailers: VehicleEntry[];
  policies: Policy[];
}

const HEAD_CLASS = "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
const CELL_CLASS = "px-6 py-5";

export function TrailersSection({
  trailers,
  policies,
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const updateTrailers = useUpdatePolicyTrailers();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<PolicyVehicle | null>(null);
  const [editPolicyId, setEditPolicyId] = useState<string | null>(null);

  function handleAdd() {
    setEditData(null);
    setEditPolicyId(null);
    setDialogOpen(true);
  }

  function handleEdit(entry: VehicleEntry) {
    setEditData(entry.vehicle);
    setEditPolicyId(entry.policyId);
    setDialogOpen(true);
  }

  async function handleRemove(entry: VehicleEntry) {
    const vin = entry.vehicle.vin;
    const label = vin ? `Trailer VIN ${vin}` : "this trailer";
    const ok = await confirm({
      title: "Remove Trailer",
      description: `Remove ${label} from the policy?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    const policy = policies.find((p) => p.id === entry.policyId);
    if (!policy) return;
    const current = (policy.trailers as PolicyVehicle[] | null) ?? [];
    const next = current.filter(
      (v) => !(v.vin === entry.vehicle.vin && v.addedAt === entry.vehicle.addedAt),
    );
    try {
      await updateTrailers.mutateAsync({ id: policy.id, trailers: next });
      toast({ title: "Removed" });
    } catch (err) {
      toast({
        title: "Failed to remove",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  }

  return (
    <Card className="overflow-hidden" data-testid="trailers-section">
      <AssetSectionHeader
        icon={Container}
        title="Trailers"
        count={trailers.length}
        testId="trailers"
        onAdd={policies.length > 0 ? handleAdd : undefined}
        disabledTooltip={policies.length === 0 ? "This insured has no policy yet — assets are stored on a policy, so add one first" : undefined}
      />
      {trailers.length === 0 ? (
        <div
          className="px-6 py-12 text-center text-sm text-muted-foreground"
          data-testid="trailers-empty"
        >
          No trailers on record.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={HEAD_CLASS}>VIN</TableHead>
                <TableHead className={HEAD_CLASS}>Make</TableHead>
                <TableHead className={HEAD_CLASS}>Year</TableHead>
                <TableHead className={HEAD_CLASS}>Body Type</TableHead>
                <TableHead className={HEAD_CLASS}>Value</TableHead>
                <TableHead className={HEAD_CLASS}>Radius</TableHead>
                <TableHead className={HEAD_CLASS}>Status</TableHead>
                <TableHead className={HEAD_CLASS}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trailers.map((entry, i) => (
                <TableRow
                  key={entry.vehicle.vin ?? `trailer-${i}`}
                  data-testid={`trailer-row-${entry.vehicle.vin ?? i}`}
                >
                  <TableCell className={`${CELL_CLASS} font-mono text-sm text-foreground`}>
                    {isNonOwnedAttachedTrailer(entry.vehicle) ? "—" : entry.vehicle.vin || "—"}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} font-medium`}>
                    {entry.vehicle.make || "—"}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                    {isNonOwnedAttachedTrailer(entry.vehicle) ? "—" : (entry.vehicle.year ?? "—")}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                    {entry.vehicle.truckType || "—"}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                    {isNonOwnedAttachedTrailer(entry.vehicle) ? "—" : vehicleValue(entry.vehicle)}
                  </TableCell>
                  <TableCell
                    className={`${CELL_CLASS} text-muted-foreground`}
                    data-testid={`trailer-radius-${entry.vehicle.vin ?? i}`}
                  >
                    {isNonOwnedAttachedTrailer(entry.vehicle) ? "—" : entry.vehicle.radius || "—"}
                  </TableCell>
                  <TableCell className={CELL_CLASS}>
                    <AssetStatusBadge status={statusForAsset(entry.removedAt, entry.active)} />
                  </TableCell>
                  <TableCell className={CELL_CLASS}>
                    <div
                      className="flex items-center gap-1"
                      data-testid={`trailer-row-action-${entry.vehicle.vin ?? i}`}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        className="cursor-pointer"
                        aria-label={`Edit Trailer VIN ${entry.vehicle.vin ?? ""}`}
                        disabled={updateTrailers.isPending}
                        onClick={() => handleEdit(entry)}
                        data-testid={`trailer-edit-${entry.vehicle.vin ?? i}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="cursor-pointer text-destructive"
                        aria-label={`Remove Trailer VIN ${entry.vehicle.vin ?? ""}`}
                        disabled={updateTrailers.isPending}
                        onClick={() => handleRemove(entry)}
                        data-testid={`trailer-remove-${entry.vehicle.vin ?? i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <VehicleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode="trailer"
        policies={policies}
        editData={editData}
        editPolicyId={editPolicyId}
      />
      {confirmDialog}
    </Card>
  );
}
